import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  ApiPerformanceReviewParticipantChartPayload,
  ApiPerformanceReviewParticipantReportFilter,
  ApiPerformanceReviewParticipantReviewersSave,
  ApiPerformanceReviewParticipantShareSettingUpdate,
  ApiPerformanceReviewStatus,
  ApiPerformanceReviewParticipantsSummaryFilter,
  ApiPerformanceReviewResultsFilter,
  ApiPerformanceReviewReviewerTypeUpdateForm,
  ApiPerformanceReviewSaveReviewerTypes,
  ApiPerformanceReviewsFilter,
  ApiPerformanceReviewUpdateDetails,
  ApiPerformanceReviewUpdateInvitation,
  ApiPerformanceReviewUpdateRatings,
  ApiPerformanceReviewUpdateReviewerTypeSettings,
  ApiPRReviewReopen,
  ApiPerformanceReviewParticipantSearchFilter,
} from "@/types/performance-review.types.ts";
import { apiPerformanceReviews } from "@/api/performance-review.api.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ReminderSettingsPayload } from "@/types/reminder.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { employeePerformanceReviewKey } from "@/queries/employees/use-employee-performance-review.query.ts";
import { useMemo, useState } from "react";

export const performanceReviewsKeys = createQueryKeys("performanceReviews", {
  details: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      const result = await apiPerformanceReviews.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  search: (filter: ApiPerformanceReviewsFilter) => ({ queryKey: [filter] }),
  stats: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      const result = await apiPerformanceReviews.getStats(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  setup: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      const result = await apiPerformanceReviews.getByIdEdit(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  getReviewerPreviewCount: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      const result = await apiPerformanceReviews.getReviewerPreviewCount(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  participantsSummarySearch: (
    id: number,
    filter?: ApiPerformanceReviewParticipantsSummaryFilter
  ) => ({
    queryKey: [id, filter],
  }),
  participantReport: (
    id: number | undefined,
    employee_id: number | undefined,
    filter: ApiPerformanceReviewParticipantReportFilter
  ) => ({
    queryKey: [id, employee_id, filter],
    queryFn: async ({ signal }) => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      if (employee_id == undefined)
        throw new ApiPassException("Performance Review employee id is required");
      const result = await apiPerformanceReviews.participantReportByEmployee(
        id,
        employee_id,
        filter,
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  participantSummaryDetails: (id: number | undefined, employee_id: number | undefined) => ({
    queryKey: [id, employee_id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      if (employee_id == undefined)
        throw new ApiPassException("Performance Review participant employee id is required");
      const result = await apiPerformanceReviews.participantSummaryByEmployee(id, employee_id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  reviewerTypeAggregatedResults: (
    id: number | undefined,
    reviewer_type_id: number | undefined
  ) => ({
    queryKey: [id, reviewer_type_id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      if (reviewer_type_id == undefined)
        throw new ApiPassException("Performance Review Reviewer Type id is required");

      const result = await apiPerformanceReviews.reviewerTypeGetAggregatedResults(
        id,
        reviewer_type_id
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  reviewerResults: (reviewerTypeId: number, respondentUuid: string) => ({
    queryKey: [reviewerTypeId, respondentUuid],
    queryFn: async () => {
      const result = await apiPerformanceReviews.reviewerResults(reviewerTypeId, respondentUuid);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  questionResultsSearchInfinite: (filter: ApiPerformanceReviewResultsFilter) => ({
    queryKey: [filter],
  }),
  participantReviewers: (
    id: number | undefined,
    employee_id: number | undefined,
    reviewer_type_id: number | undefined
  ) => ({
    queryKey: [id, employee_id, reviewer_type_id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Performance Review id is required");
      if (reviewer_type_id == undefined)
        throw new ApiPassException("Performance Review Reviewer Type id is required");
      if (employee_id == undefined)
        throw new ApiPassException("Performance Review participant employee id is required");

      const result = await apiPerformanceReviews.getParticipantReviewers(
        id,
        employee_id,
        reviewer_type_id
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  participantChart: (id: number, payload: ApiPerformanceReviewParticipantChartPayload) => ({
    queryKey: [id, payload],
  }),
  searchParticipantChart: (id: number, payload: ApiPerformanceReviewParticipantChartPayload) => ({
    queryKey: [id, payload],
  }),
  participantSearch: (filter: ApiPerformanceReviewParticipantSearchFilter) => ({
    queryKey: [filter],
  }),
});

export const usePerformanceReviewDetails = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...performanceReviewsKeys.details(id),
    enabled: !!id && enabled,
  });
};

export const usePerformanceReviewsSearch = (
  filter: ApiPerformanceReviewsFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...performanceReviewsKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiPerformanceReviews.search(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const usePerformanceReviewsIsRunningSearchInline = (
  enabled: boolean = true,
  defaultFilter?: ApiPerformanceReviewsFilter
) => {
  const [filter, setFilter] = useState<ApiPerformanceReviewsFilter>({
    page_number: 1,
    ...(defaultFilter ?? {}),
    statuses: [ApiPerformanceReviewStatus.running],
  });

  const requestFilter = useMemo<ApiPerformanceReviewsFilter>(
    () => ({
      ...filter,
      statuses: [ApiPerformanceReviewStatus.running],
    }),
    [filter]
  );

  const { data, isLoading, isFetching } = usePerformanceReviewsSearch(requestFilter, enabled);

  const options: SelectItem[] = useMemo(() => {
    return (
      data?.items.map((item) => ({
        id: item.id,
        title: item.title,
      })) ?? []
    );
  }, [data]);

  function search(text: string | undefined) {
    setFilter((currentFilter) => ({
      ...currentFilter,
      page_number: 1,
      text: text?.trim() || undefined,
      statuses: [ApiPerformanceReviewStatus.running],
    }));
  }

  return {
    data,
    isLoading,
    isFetching,
    options,
    search,
    filter,
    setFilter,
  };
};

export const usePerformanceReviewStats = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...performanceReviewsKeys.stats(id),
    enabled: !!id && enabled,
  });
};

export const usePerformanceReviewLaunch = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiPerformanceReviews.launch(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.details(payload.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewPause = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiPerformanceReviews.pause(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.details(payload.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewComplete = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiPerformanceReviews.complete(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.details(payload.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewReopen = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ApiPRReviewReopen }) {
      const result = await apiPerformanceReviews.reopen(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.details(payload.id).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewDelete = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiPerformanceReviews.delete(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.setup._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.search._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewSetup = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...performanceReviewsKeys.setup(id),
    enabled: !!id && enabled,
  });
};

export const usePerformanceReviewCreate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function () {
      const result = await apiPerformanceReviews.create();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.search._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewUpdateDetails = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiPerformanceReviewUpdateDetails;
    }) {
      const result = await apiPerformanceReviews.updateDetails(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantSummaryDetails._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewSaveReviewerTypes = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiPerformanceReviewSaveReviewerTypes;
    }) {
      const result = await apiPerformanceReviews.saveReviewerTypes(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantSummaryDetails._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewerPreviewCount = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...performanceReviewsKeys.getReviewerPreviewCount(id),
    enabled: !!id && enabled,
  });
};

export const usePerformanceReviewReviewerTypeUpdateForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      id,
      reviewer_type_id,
      payload,
    }: {
      id: number;
      reviewer_type_id: number;
      payload: ApiPerformanceReviewReviewerTypeUpdateForm;
    }) {
      const result = await apiPerformanceReviews.updateReviewerTypeForm(
        id,
        reviewer_type_id,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewInvalidateSetup = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
  };
};

export const usePerformanceReviewUpdateReviewerTypeSettings = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiPerformanceReviewUpdateReviewerTypeSettings;
    }) => {
      const result = await apiPerformanceReviews.updateReviewerTypeSettings(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantSummaryDetails._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewUpdateInvitation = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiPerformanceReviewUpdateInvitation;
    }) => {
      const result = await apiPerformanceReviews.updateInvitation(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewUpdateRatings = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiPerformanceReviewUpdateRatings;
    }) {
      const result = await apiPerformanceReviews.updateRatings(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantSummaryDetails._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewUpdateReminders = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: { id: number; payload: ReminderSettingsPayload }) {
      const result = await apiPerformanceReviews.updateReminders(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceReviewsKeys.setup._def });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantSummaryDetails._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewParticipantsSummarySearch = (
  id: number,
  filter: ApiPerformanceReviewParticipantsSummaryFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...performanceReviewsKeys.participantsSummarySearch(id, filter),
    queryFn: async ({ signal }) => {
      if (!filter) return;
      const result = await apiPerformanceReviews.participantsSummarySearch(id, filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled: enabled,
    staleTime: 60 * 1000,
  });
};

export const usePerformanceReviewParticipantSummaryDetails = (
  id: number | undefined,
  employee_id: number | undefined,
  enabled = true
) => {
  return useQuery({
    ...performanceReviewsKeys.participantSummaryDetails(id, employee_id),
    enabled: !!id && enabled,
    staleTime: 60 * 1000,
  });
};

export const usePerformanceReviewParticipantReport = ({
  id,
  employee_id,
  filter,
  enabled = true,
}: {
  id: number | undefined;
  employee_id: number | undefined;
  filter: ApiPerformanceReviewParticipantReportFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    ...performanceReviewsKeys.participantReport(id, employee_id, filter),
    enabled: !!id && !!employee_id && enabled,
    staleTime: 60 * 1000,
  });
};

export const useParticipantShareSettingUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      id,
      employee_id,
      payload,
    }: {
      id: number;
      employee_id: number;
      payload: ApiPerformanceReviewParticipantShareSettingUpdate;
    }) {
      const result = await apiPerformanceReviews.participantShareSettingUpdateByEmployee(
        id,
        employee_id,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewReviewerTypeAggregatedResults = (
  id: number | undefined,
  reviewer_type_id: number | undefined,
  enabled = true
) => {
  return useQuery({
    ...performanceReviewsKeys.reviewerTypeAggregatedResults(id, reviewer_type_id),
    enabled: !!id && !!reviewer_type_id && enabled,
    staleTime: 60 * 1000,
  });
};

export const usePerformanceReviewReviewerResults = ({
  reviewerTypeId,
  respondentUuid,
  enabled = true,
}: {
  reviewerTypeId: number;
  respondentUuid: string;
  enabled?: boolean;
}) => {
  return useQuery({
    ...performanceReviewsKeys.reviewerResults(reviewerTypeId, respondentUuid),
    enabled: enabled,
  });
};

export const usePerformanceReviewQuestionResultsSearchInfinite = (
  filter: ApiPerformanceReviewResultsFilter
) => {
  return useInfiniteQuery({
    queryKey: performanceReviewsKeys.questionResultsSearchInfinite(filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiPerformanceReviews.questionResultsSearch(
        { ...filter, page_number: pageParam },
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
    staleTime: 60 * 1000,
  });
};

export const useParticipantReviewers = (
  id: number | undefined,
  employee_id: number | undefined,
  reviewer_type_id: number | undefined,
  enabled = true
) => {
  return useQuery({
    ...performanceReviewsKeys.participantReviewers(id, employee_id, reviewer_type_id),
    enabled: !!id && !!employee_id && !!reviewer_type_id && enabled,
    staleTime: 60 * 1000,
  });
};

export const useParticipantReviewersSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      id,
      employee_id,
      reviewer_type_id,
      payload,
    }: {
      id: number;
      employee_id: number;
      reviewer_type_id: number;
      payload: ApiPerformanceReviewParticipantReviewersSave;
    }) {
      const result = await apiPerformanceReviews.saveParticipantReviewers(
        id,
        employee_id,
        reviewer_type_id,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReviewers._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantSummaryDetails._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeePerformanceReviewKey.myAwaitingReviewerInvite._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useParticipantChart = (
  id: number,
  payload: ApiPerformanceReviewParticipantChartPayload,
  enabled = true
) => {
  return useQuery({
    ...performanceReviewsKeys.participantChart(id, payload),
    queryFn: async ({ signal }) => {
      const result = await apiPerformanceReviews.getParticipantChart(id, payload, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled: enabled,
    staleTime: 60 * 1000,
  });
};

export const useParticipantChartSearch = (
  id: number,
  payload: ApiPerformanceReviewParticipantChartPayload,
  enabled = true
) => {
  return useQuery({
    ...performanceReviewsKeys.searchParticipantChart(id, payload),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiPerformanceReviews.searchParticipantChart(id, payload, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

const useParticipantSearch = (
  filter: ApiPerformanceReviewParticipantSearchFilter,
  enabled = true
) => {
  return useQuery({
    queryKey: performanceReviewsKeys.participantSearch(filter).queryKey,
    queryFn: async () => {
      const result = await apiPerformanceReviews.participantSearch(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled: enabled,
  });
};

export const useParticipantSearchInline = (enabled = true) => {
  const { renderEmployeeName } = useFormatter();
  const [filter, setFilter] = useState<ApiPerformanceReviewParticipantSearchFilter>({
    text: "",
    page_number: 1,
  });

  const { data, isLoading } = useParticipantSearch(filter, enabled);

  const options: SelectItem[] = useMemo(() => {
    return (
      data?.items.map((p) => ({
        id: p.employee.id,
        title: renderEmployeeName(p.employee),
      })) ?? []
    );
  }, [data, renderEmployeeName]);

  function search(text: string | undefined) {
    setFilter((prev) => ({ ...prev, text: text ?? "", page_number: 1 }));
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useLeaveFeedbackMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { reviewId: number; employeeId: number; reviewTypeId: number }) => {
      const result = await apiPerformanceReviews.addLeaveFeedback(
        payload.reviewId,
        payload.employeeId,
        payload.reviewTypeId
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantSearch._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantsSummarySearch._def,
      });
    },
  });
};
